import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

import { ErroreApi, ErroreRete, api } from '../lib/api';
import { giaInstallata } from '../lib/installazione';
import { TEMA_PREDEFINITO } from '../lib/temi';
import type { Tema } from '../lib/temi';
import type { Profilo, Ruolo } from '../lib/tipi';

/**
 * Stato della sessione, condiviso da tutta l'applicazione.
 *
 * Il blocco per inattività è rispecchiato anche qui con un contatore locale, perché lo
 * schermo deve oscurarsi allo scadere del tempo senza aspettare la prossima richiesta. Il
 * server resta l'arbitro: se il client sbagliasse, ogni chiamata riceverebbe comunque
 * `401 sessione_bloccata`.
 */

type Stato = 'caricamento' | 'anonimo' | 'scaduta' | 'attiva' | 'senza_rete';

interface ValoreSessione {
  stato: Stato;
  profilo: Profilo | null;
  bloccata: boolean;
  /** 9b — il server non risponde: senza rete l'applicazione non funziona. */
  senzaRete: boolean;
  /** Profilo dell'ultima sessione valida: serve alla 6i per dire da che ora era aperta. */
  precedente: Profilo | null;
  entra: (username: string, password: string) => Promise<Profilo>;
  sblocca: (password: string) => Promise<void>;
  esci: () => Promise<void>;
  aggiorna: () => Promise<void>;
  /** Da invocare dopo il cambio password al primo accesso. */
  ricarica: () => Promise<void>;
  /**
   * Cambia il colore dell'interfaccia. Si vede subito e si salva dopo; se il salvataggio
   * non riesce, il colore torna quello di prima e l'errore arriva a chi ha chiamato.
   */
  cambiaTema: (tema: Tema) => Promise<void>;
  /**
   * Indossa una veste, cioè cambia il ruolo con cui si opera. `null` torna al proprio.
   *
   * A differenza del tema qui non si anticipa nulla: il tema cambia un colore, la veste
   * cambia quale area l'applicazione disegna. Mostrarla prima della conferma vorrebbe dire
   * far comparire per un istante un'intera interfaccia diversa, e riportarla indietro se il
   * server dicesse di no — con il rischio, peggiore, che qualcuno faccia in tempo a premere
   * qualcosa che non gli è concesso.
   */
  cambiaVeste: (veste: Ruolo | null) => Promise<void>;
  /**
   * Sostituisce il profilo con quello appena tornato dal server.
   *
   * Serve alle rotte che rispondono con il profilo intero — l'immagine, e domani qualunque
   * altra preferenza — perché il client tenga un oggetto solo invece di ricomporlo
   * indovinando cosa è cambiato. `cambiaTema` e `cambiaVeste` esistevano già e facevano
   * ciascuna la propria copia di questa riga; ora la fanno passando di qui.
   */
  aggiornaProfilo: (profilo: Profilo) => Promise<void>;
}

const Contesto = createContext<ValoreSessione | null>(null);

export function useSessione(): ValoreSessione {
  const valore = useContext(Contesto);
  if (!valore) throw new Error('useSessione fuori da FornitoreSessione');
  return valore;
}

/** `navigator.maxTouchPoints`: unico segnale che distingue iPadOS da macOS. */
function puntiTocco(): number {
  return typeof navigator === 'undefined' ? 0 : (navigator.maxTouchPoints ?? 0);
}

export function FornitoreSessione({ children }: { children: ReactNode }) {
  const [stato, setStato] = useState<Stato>('caricamento');
  const [profilo, setProfilo] = useState<Profilo | null>(null);
  const [precedente, setPrecedente] = useState<Profilo | null>(null);
  const [bloccataLocale, setBloccataLocale] = useState(false);

  const ultimaAttivita = useRef<number>(Date.now());

  /*
   * C'è mai stata una sessione viva in questa scheda?
   *
   * È la sola cosa che distingue «la tua sessione è scaduta» da «non sei ancora entrato»,
   * e le due si somigliano più di quanto sembri: il server risponde 401 in entrambi i casi,
   * perché in entrambi i casi non c'è un cookie valido. Senza questo dato la 6i finiva per
   * comparire a chi stava semplicemente guardando la pagina di accesso.
   *
   * Si spegne solo con l'uscita volontaria: dopo un logout il 401 successivo è di nuovo
   * «non sei entrato», non una scadenza.
   */
  const avutaSessione = useRef(false);

  const applica = useCallback((p: Profilo) => {
    setProfilo(p);
    setPrecedente(p);
    setStato('attiva');
    setBloccataLocale(p.bloccata);
    avutaSessione.current = true;
    ultimaAttivita.current = Date.now();
  }, []);

  const aggiorna = useCallback(async () => {
    try {
      const p = await api.get<Profilo>('/auth/me');

      /*
       * Una risposta 200 che non è un profilo.
       *
       * Non è teoria: basta che l'applicazione venga servita senza le sue funzioni — un
       * `vite` senza `wrangler`, un deploy a metà, un portale captive che intercetta —
       * e `/auth/me` restituisce l'`index.html` con stato 200. Prima di questo controllo
       * il provider leggeva `p.bloccata` su `null` e schiantava l'intera applicazione:
       * schermo bianco, comprese le pagine che con la sessione non c'entrano nulla.
       *
       * Trattarla come «nessuna sessione» è la lettura giusta: non c'è un profilo, quindi
       * non c'è nessuno collegato.
       */
      if (!p || typeof p !== 'object' || typeof p.ruolo !== 'string') {
        setProfilo(null);
        setStato('anonimo');
        return;
      }

      setProfilo(p);
      setPrecedente(p);
      setStato('attiva');
      avutaSessione.current = true;
      // Un aggiornamento in sottofondo può accorgersi che il server considera bloccata la
      // sessione, ma non deve sbloccare quella che il contatore locale ha già bloccato.
      setBloccataLocale((prima) => prima || p.bloccata);
    } catch (e) {
      // 9b — il server non risponde. Non si conclude nulla sulla sessione: si dice che
      // manca la rete e si offre di riprovare.
      if (e instanceof ErroreRete) {
        setStato('senza_rete');
        return;
      }
      if (e instanceof ErroreApi && e.stato === 401) {
        /*
         * Un 401 non è di per sé una scadenza: è la risposta normale per chiunque non abbia
         * un cookie valido, e chi sta guardando la pagina di accesso è esattamente in quel
         * caso. Scaduta lo è solo una sessione che prima c'era davvero.
         *
         * La regola stava scritta sullo stato precedente — «se non era in caricamento
         * allora è scaduta» — e prendeva dentro anche `anonimo`, che è lo stato di chi non
         * è mai entrato. Bastava tornare sulla scheda del login, che fa ripartire
         * `aggiorna` da `visibilitychange`, per vedersi annunciare la scadenza di una
         * sessione mai aperta. Sui computer di segreteria, dove la pagina di accesso resta
         * aperta mentre si fa altro, era la norma e non l'eccezione.
         */
        setProfilo(null);
        setStato(avutaSessione.current ? 'scaduta' : 'anonimo');
        return;
      }
      throw e;
    }
  }, []);

  useEffect(() => {
    void aggiorna();
  }, [aggiorna]);

  // Tornando sulla scheda si ricontrolla la sessione: se nel frattempo è scaduta, va
  // mostrata la 6i invece di lasciare a schermo un'interfaccia che non funziona più.
  useEffect(() => {
    const suVisibilita = () => {
      if (document.visibilityState === 'visible') void aggiorna();
    };
    document.addEventListener('visibilitychange', suVisibilita);
    return () => document.removeEventListener('visibilitychange', suVisibilita);
  }, [aggiorna]);

  // ── blocco per inattività, rispecchiato lato client ────────────────────────────────
  useEffect(() => {
    if (stato !== 'attiva' || !profilo) return;
    /*
     * `minuti_blocco` nullo significa che lo schermo non si blocca: è il regime lungo
     * dell'applicazione installata, e lo decide il server.
     *
     * Va rispettato **qui**, e non basta che il server non consideri più bloccata la
     * sessione: questo orologio blocca per conto suo, senza chiedere niente a nessuno, e
     * lasciato acceso metterebbe la schermata della password davanti a un socio a cui il
     * server non la sta chiedendo affatto.
     */
    // In una costante, non letto dentro l'intervallo: il compilatore non sa che `profilo`
    // non cambia fra il controllo qui sopra e la lettura dentro la chiusura.
    const soglia = profilo.minuti_blocco;
    if (soglia === null) return;

    const segnala = () => {
      ultimaAttivita.current = Date.now();
    };
    const eventi = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
    for (const e of eventi) window.addEventListener(e, segnala, { passive: true });

    const orologio = window.setInterval(() => {
      const fermi = (Date.now() - ultimaAttivita.current) / 60_000;
      if (fermi >= soglia) setBloccataLocale(true);
    }, 15_000);

    return () => {
      for (const e of eventi) window.removeEventListener(e, segnala);
      window.clearInterval(orologio);
    };
  }, [stato, profilo]);

  const entra = useCallback(
    async (username: string, password: string) => {
      const p = await api.post<Profilo>('/auth/login', {
        username,
        password,
        punti_tocco: puntiTocco(),
        // Se l'applicazione è installata la sessione diventa lunga e non si blocca più —
        // ma solo per i soci, e lo decide il server: qui si dichiara soltanto il fatto.
        installata: giaInstallata(),
      });
      applica(p);
      return p;
    },
    [applica],
  );

  // 6g, 6h — stessa sessione, nessun nuovo cookie: si invia la sola password.
  const sblocca = useCallback(
    async (password: string) => {
      const p = await api.post<Profilo>('/auth/login', { password });
      applica(p);
    },
    [applica],
  );

  const esci = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setProfilo(null);
      setPrecedente(null);
      setBloccataLocale(false);
      // Chi esce di sua volontà non ha una sessione scaduta: da qui in poi i 401 tornano a
      // voler dire «non sei entrato», e la 6i non deve più comparire.
      avutaSessione.current = false;
      setStato('anonimo');
    }
  }, []);

  const ricarica = useCallback(async () => {
    applica(await api.get<Profilo>('/auth/me'));
  }, [applica]);

  /**
   * L'unico punto che scrive `data-tema` sul documento.
   *
   * Non c'è alcuna copia locale della preferenza — niente `localStorage`, niente cookie —
   * perché il tema è dell'account e non del vetro: due persone sulla stessa postazione
   * devono vederne due diversi, e la stessa persona su due dispositivi lo stesso. Il
   * prezzo è che il colore arriva solo dopo `/auth/me`: fino a lì, e per chi non ha fatto
   * l'accesso, vale il verde di `:root`.
   */
  useEffect(() => {
    document.documentElement.dataset.tema = profilo?.tema ?? TEMA_PREDEFINITO;
  }, [profilo?.tema]);

  const cambiaTema = useCallback(
    async (tema: Tema) => {
      const prima = profilo;
      if (!prima || prima.tema === tema) return;

      // Prima si vede, poi si salva: il selettore deve rispondere sotto il dito.
      setProfilo({ ...prima, tema });
      try {
        const aggiornato = await api.put<Profilo>('/me/tema', { tema });
        setProfilo(aggiornato);
        setPrecedente(aggiornato);
      } catch (e) {
        // Nessuna interfaccia in uno stato che il server non conosce.
        setProfilo(prima);
        throw e;
      }
    },
    [profilo],
  );

  const cambiaVeste = useCallback(
    async (veste: Ruolo | null) => {
      const aggiornato = await api.put<Profilo>('/me/veste', { veste });
      setProfilo(aggiornato);
      setPrecedente(aggiornato);
    },
    [],
  );

  const aggiornaProfilo = useCallback(async (aggiornato: Profilo) => {
    setProfilo(aggiornato);
    setPrecedente(aggiornato);
  }, []);

  const valore = useMemo<ValoreSessione>(
    () => ({
      stato,
      profilo,
      bloccata: bloccataLocale,
      senzaRete: stato === 'senza_rete',
      precedente,
      entra,
      sblocca,
      esci,
      aggiorna,
      ricarica,
      cambiaTema,
      cambiaVeste,
      aggiornaProfilo,
    }),
    [
      stato,
      profilo,
      bloccataLocale,
      precedente,
      entra,
      sblocca,
      esci,
      aggiorna,
      ricarica,
      cambiaTema,
      cambiaVeste,
      aggiornaProfilo,
    ],
  );

  return <Contesto.Provider value={valore}>{children}</Contesto.Provider>;
}
